/** Must match the request-size guard in the generateReasons function. */
export const MAX_GENERATE_REASONS_REQUEST_BYTES = 2_000

type GenerateReasonsRequestPayload = {
  optionLabel: string
  optionId: string
  reasons?: unknown[]
  language?: string
}

/**
 * Measures the JSON payload exactly as fetch sends it, including UTF-8
 * expansion for non-ASCII text.
 */
export function getGenerateReasonsRequestSize(
  request: GenerateReasonsRequestPayload,
): number {
  const body = JSON.stringify(request)

  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(body).byteLength
  }

  // Jest's JSDOM environment does not provide TextEncoder. JSON.stringify
  // escapes lone surrogates, so this fallback has the same UTF-8 byte count.
  return unescape(encodeURIComponent(body)).length
}

export function isGenerateReasonsRequestWithinSizeLimit(
  request: GenerateReasonsRequestPayload,
): boolean {
  return (
    getGenerateReasonsRequestSize(request) <= MAX_GENERATE_REASONS_REQUEST_BYTES
  )
}

/**
 * Existing-option generation sends only that option's first ten reasons.
 * Treat any oversized request as a list-level generation limitation.
 */
export function hasOversizedExistingReasonGenerationRequest(
  options: readonly { id: string; label: string }[],
  reasons: readonly { optionId: string }[],
  language: string,
): boolean {
  return options.some(
    (option) =>
      !isGenerateReasonsRequestWithinSizeLimit({
        optionLabel: option.label,
        optionId: option.id,
        reasons: reasons
          .filter((reason) => reason.optionId === option.id)
          .slice(0, 10),
        language,
      }),
  )
}
